import { type JSX, useCallback, useEffect, useState } from 'react';

import { ApiFehler } from '../api/fehler';
import {
  type Wartelisteneintrag,
  type Zeitraum,
  ZEITRAEUME_WOCHEN,
  eintragen,
  liste,
  loeschen,
} from '../api/warteliste';
import { Abschnitt } from '../komponenten/Akte';
import { Meldung } from '../komponenten/Meldung';
import { Rueckfrage } from '../komponenten/Rueckfrage';
import { alsTag, alsZeitpunkt } from '../datum';

const HERKUNFTNAME: Readonly<Record<Wartelisteneintrag['herkunft'], string>> = {
  portal: 'Portal',
  innendienst: 'Innendienst',
  ueberfuehrt: 'übernommen',
};

/**
 * Der letzte Tag, an dem der Eintrag gilt. `gueltigBis` ist ausschließlich —
 * „bis 01.12." hieße sonst einen Tag zu lang.
 */
function letzterTag(gueltigBis: string): string {
  return alsTag(new Date(new Date(gueltigBis).getTime() - 1).toISOString()) ?? '';
}

/**
 * DIE WARTELISTE EINES TRAININGS (BUC-11, DAT-21 nach ÄA-55).
 *
 * Sie steht am Produkt und nicht am Termin: Wer wartet, wartet auf irgendeinen
 * Termin dieses Trainings in seinem Zeitraum. Die Mail zum freien Platz
 * schickt der Server von selbst; einen Knopf dafür gibt es nicht.
 *
 * Unbestätigte Einträge stehen mit da, weil der Innendienst sonst eine
 * Adresse ein zweites Mal einträgt, deren Bestätigung nur im Postfach liegt.
 */
export function Warteliste({
  artikel,
  angeboten,
  darfFuehren,
}: {
  readonly artikel: string;
  /** Der Schalter am Produkt — ohne ihn nimmt der Server keinen Eintrag an. */
  readonly angeboten: boolean;
  /** `buchung:anlegen` — eintragen und löschen. */
  readonly darfFuehren: boolean;
}): JSX.Element {
  const [eintraege, setzeEintraege] = useState<readonly Wartelisteneintrag[] | undefined>(undefined);
  const [fehler, setzeFehler] = useState<ApiFehler | undefined>(undefined);
  const [meldung, setzeMeldung] = useState<string | null>(null);
  const [laeuft, setzeLaeuft] = useState(false);
  const [email, setzeEmail] = useState('');
  /* Als Text: Ein Zahlfeld, das beim Leeren auf 1 springt, macht aus „3" eine 13. */
  const [plaetze, setzePlaetze] = useState('1');
  const [wochen, setzeWochen] = useState<Zeitraum>(8);
  const [anrede, setzeAnrede] = useState('');
  const [name, setzeName] = useState('');
  /* CRM-104: der Eintrag, dessen Löschen im Pop-up nachfragt. */
  const [zuLoeschen, setzeZuLoeschen] = useState<Wartelisteneintrag | null>(null);

  const laden = useCallback(async (): Promise<void> => {
    try {
      setzeEintraege(await liste(artikel));
      setzeFehler(undefined);
    } catch (ursache) {
      if (!(ursache instanceof ApiFehler)) throw ursache;
      setzeFehler(ursache);
    }
  }, [artikel]);

  useEffect(() => {
    void laden();
  }, [laden]);

  async function trageEin(): Promise<void> {
    setzeLaeuft(true);
    setzeFehler(undefined);
    setzeMeldung(null);
    try {
      await eintragen({
        email: email.trim(),
        produkt: artikel,
        plaetze: Number(plaetze),
        wochen,
        ...(anrede.trim() === '' ? {} : { anrede: anrede.trim() }),
        ...(name.trim() === '' ? {} : { name: name.trim() }),
      });
      setzeMeldung(
        `${email.trim()} ist eingetragen. Die Bestätigungsmail ist unterwegs; `
          + 'erst nach dem Klick auf den Link kommen Mails zu freien Plätzen.',
      );
      setzeEmail('');
      setzeAnrede('');
      setzeName('');
      setzePlaetze('1');
      await laden();
    } catch (ursache) {
      if (!(ursache instanceof ApiFehler)) throw ursache;
      setzeFehler(ursache);
    } finally {
      setzeLaeuft(false);
    }
  }

  /** Wirft (CRM-104): Der Fehler steht in der Rückfrage, und sie bleibt offen. */
  async function loesche(e: Wartelisteneintrag): Promise<void> {
    setzeLaeuft(true);
    setzeFehler(undefined);
    setzeMeldung(null);
    try {
      await loeschen(e.id);
    } finally {
      setzeLaeuft(false);
    }
    setzeZuLoeschen(null);
    setzeMeldung(`${e.email} steht nicht mehr auf der Warteliste.`);
    await laden();
  }

  if (eintraege === undefined) {
    return fehler !== undefined ? <Meldung fehler={fehler} /> : <p role="status">Wird geladen …</p>;
  }

  const bestaetigt = eintraege.filter((e) => e.bestaetigtAm !== null);
  const plaetzeGueltig = /^\d{1,3}$/.test(plaetze) && Number(plaetze) >= 1 && Number(plaetze) <= 200;

  // BUC-11
  return (
    <Abschnitt titel="Warteliste" zusatz={bestaetigt.length}>
      {fehler !== undefined && <Meldung fehler={fehler} />}
      {meldung !== null && (
        <p className="hinweiszeile" role="status">
          {meldung}
        </p>
      )}

      {eintraege.length === 0 ? (
        <p className="feld__hilfe">Auf dieses Training wartet niemand.</p>
      ) : (
        <div className="tabellenhuelle tabellenhuelle--telefon">
          <table className="liste__tabelle">
            <thead>
              <tr>
                <th scope="col">E-Mail</th>
                <th scope="col">Plätze</th>
                <th scope="col">Zeitraum</th>
                <th scope="col">Stand</th>
                <th scope="col">Zuletzt benachrichtigt</th>
                {darfFuehren && <th scope="col" aria-label="Handlungen" />}
              </tr>
            </thead>
            <tbody>
              {eintraege.map((e) => (
                <tr key={e.id}>
                  <td>
                    {e.email}
                    {e.name !== null && <div className="feld__hilfe">{e.name}</div>}
                  </td>
                  <td>{e.plaetze}</td>
                  <td>
                    {e.wochen} Wochen
                    <div className="feld__hilfe">bis {letzterTag(e.gueltigBis)}</div>
                  </td>
                  <td>
                    {/* DAT-21: Erst die Bestätigung macht den Eintrag wirksam. */}
                    {e.bestaetigtAm === null ? (
                      <span className="marke marke--warn">Bestätigung aussteht</span>
                    ) : (
                      <span className="marke marke--gut">bestätigt</span>
                    )}
                    <div className="feld__hilfe">
                      {HERKUNFTNAME[e.herkunft]} · {alsTag(e.eingetragenAm)}
                    </div>
                  </td>
                  <td>
                    {e.zuletztBenachrichtigtAm === null
                      ? '—'
                      : alsZeitpunkt(e.zuletztBenachrichtigtAm)}
                    {e.benachrichtigungen > 0 && (
                      <div className="feld__hilfe">
                        {e.benachrichtigungen === 1 ? '1 Termin' : `${String(e.benachrichtigungen)} Termine`}
                      </div>
                    )}
                  </td>
                  {darfFuehren && (
                    <td>
                      <button
                        type="button"
                        className="knopf knopf--eingriff knopf--klein"
                        disabled={laeuft}
                        onClick={() => setzeZuLoeschen(e)}
                      >
                        Löschen …
                      </button>
                    </td>
                  )}
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}

      {/*
        BUC-11: Ob das Produkt die Warteliste anbietet, sagt die Maske vorher.
        Der Server prüft es beim Eintragen trotzdem (warteliste_nicht_angeboten)
        — der Schalter kann zwischen Laden und Eintragen umgelegt worden sein.
      */}
      {darfFuehren && !angeboten && (
        <p className="feld__hilfe">
          Für dieses Training wird keine Warteliste angeboten. Eintragen lässt sich erst, wenn
          „Warteliste anbieten“ eingeschaltet ist.
        </p>
      )}
      {darfFuehren && angeboten && (
        <>
          <div className="feldreihe">
            <div className="feld">
              <label className="feld__beschriftung" htmlFor="wlEmail">
                E-Mail
              </label>
              <input
                id="wlEmail"
                type="email"
                className="feld__eingabe"
                maxLength={320}
                value={email}
                onChange={(ev) => setzeEmail(ev.target.value)}
              />
            </div>
            <div className="feld">
              <label className="feld__beschriftung" htmlFor="wlPlaetze">
                Plätze
              </label>
              <input
                id="wlPlaetze"
                type="number"
                className="feld__eingabe"
                min={1}
                max={200}
                value={plaetze}
                onChange={(ev) => setzePlaetze(ev.target.value)}
              />
            </div>
            <div className="feld">
              <label className="feld__beschriftung" htmlFor="wlWochen">
                Zeitraum
              </label>
              <select
                id="wlWochen"
                className="feld__eingabe"
                value={wochen}
                onChange={(ev) => setzeWochen(Number(ev.target.value) as Zeitraum)}
              >
                {ZEITRAEUME_WOCHEN.map((w) => (
                  <option key={w} value={w}>
                    {w} Wochen
                  </option>
                ))}
              </select>
            </div>
          </div>
          <div className="feldreihe">
            <div className="feld">
              <label className="feld__beschriftung" htmlFor="wlAnrede">
                Anrede
              </label>
              <input
                id="wlAnrede"
                type="text"
                className="feld__eingabe"
                maxLength={40}
                value={anrede}
                onChange={(ev) => setzeAnrede(ev.target.value)}
              />
            </div>
            <div className="feld">
              <label className="feld__beschriftung" htmlFor="wlName">
                Name
              </label>
              <input
                id="wlName"
                type="text"
                className="feld__eingabe"
                maxLength={200}
                value={name}
                onChange={(ev) => setzeName(ev.target.value)}
              />
            </div>
          </div>
          <p className="feld__hilfe">
            Auch hier geht eine Bestätigungsmail hinaus; unbestätigte Einträge werden nach
            48 Stunden gelöscht.
          </p>
          <div className="knopfreihe">
            <button
              type="button"
              className="knopf knopf--haupt"
              disabled={laeuft || email.trim() === '' || !plaetzeGueltig}
              onClick={() => void trageEin()}
            >
              Eintragen
            </button>
          </div>
        </>
      )}

      {zuLoeschen !== null && (
        <Rueckfrage
          frage="Eintrag löschen?"
          unterzeile={zuLoeschen.name === null ? zuLoeschen.email : `${zuLoeschen.email} · ${zuLoeschen.name}`}
          tat="Löschen"
          beiBestaetigen={() => loesche(zuLoeschen)}
          beiSchliessen={() => setzeZuLoeschen(null)}
        >
          <p>
            {zuLoeschen.plaetze === 1 ? '1 Platz' : `${String(zuLoeschen.plaetze)} Plätze`}, gültig bis{' '}
            {letzterTag(zuLoeschen.gueltigBis)}. Zu freien Plätzen geht an diese Adresse keine Mail
            mehr; wer wieder warten will, muss sich neu eintragen und erneut bestätigen.
          </p>
        </Rueckfrage>
      )}
    </Abschnitt>
  );
}
